import { useEffect, useReducer, useState } from 'react';
import dayjs from 'dayjs';
import Form, { RuleObject } from 'antd/lib/form';
import App from 'antd/lib/app';
import Modal from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Typography from 'antd/lib/typography';
import Checkbox from 'antd/lib/checkbox';
import Tooltip from 'antd/lib/tooltip';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DatePicker, { DatePickerProps, RangePickerProps } from 'antd/lib/date-picker';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined';
import TextArea from 'antd/lib/input/TextArea';
import { NamePath, StoreValue } from 'antd/lib/form/interface';
import { InputNumber, Select, theme } from 'antd/lib';
import { Col, Row } from 'antd/lib/grid';
import { Input } from 'antd';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { PjmSyncStatusDetailed } from '../../../PjmSyncStatusDetailed';
import { StatusTag } from 'src/components/StatusTag';
import { RegistrationCommentTable } from '../../components/RegistrationCommentTable';
import { RegistrationLocationTable } from '../../components/RegistrationLocationTable';
import {
    fetchPjmRegistration,
    usePjmRegistrationCalculateWplMutation,
    usePjmRegistrationCreateMutation,
    usePjmRegistrationSubmitMutation,
    usePjmRegistrationTerminateMutation,
    usePjmRegistrationUpdateMutation,
    usePjmRegistrationWithdrawMutation,
} from 'src/domain/pjm/queries';
import {
    PjmMeasurementMethod,
    PjmProductType,
    PjmRegistrationLeadTime,
    PjmRegistrationProgram,
    PjmRegistrationStatus,
    PjmResourceType,
} from 'src/domain/pjm/dto/enums';
import { LSE_VALUES, REGISTRATION_LEAD_EXCEPTION } from 'src/domain/pjm/values';
import { ResidentialProgramTypeSelector } from 'src/pages/pjm/PjmLocations/components/ResidentialProgramTypeSelector';
import { PjmRegistrationForm, PjmRegistrationTransformer } from './PjmRegistrationTransformer';
import { defaultFormState, reducer } from './usePjmRegistrationFormState';
import { FormItemControl } from './FormItemControl';
import { strikePrice } from 'src/domain/pjm/validators';
import './PjmRegistrationModal.css';
import { PJM_TZ } from 'src/domain/pjm';
import { PjmSeasonDto } from '../../../../../domain/pjm/season/PjmSeasonDto';
import { LatestPjmRegistrationMeterDataUploadStatus } from '../PjmRegistrationUploadMeterDataModal/LatestPjmRegistrationMeterDataUploadStatus';
import { MeterDataUploadStatus } from '../../../../../domain/pjm/meter-data-upload/interface';
import {
    useLatestPjmMeterDataUploadByRegistration,
    useSchedulePjmMeterDataUploadFileGenerationMutation,
} from '../../../../../domain/pjm/meter-data-upload/queries';
import { isRegistrationActionAllowed } from '../../isRegistrationActionAllowed';

dayjs.extend(utc);
dayjs.extend(timezone);

const programOptions = Object.values(PjmRegistrationProgram).map(it => ({ label: it, value: it }));
const resourceTypeOptions = Object.values(PjmResourceType).map(it => ({ label: it, value: it }));
const measurementMethodOptions = Object.values(PjmMeasurementMethod).map(it => ({ label: it, value: it }));

let productOptions = Object.values(PjmProductType).map(it => ({ label: it, value: it }));
/** Load Management DR programs we should show only CAPACITY_PERFORMANCE_DR product */
productOptions = productOptions.filter(it => it.value === PjmProductType.CAPACITY_PERFORMANCE_DR);

const leadTimeOptions = Object.values(PjmRegistrationLeadTime).map(it => ({ label: it, value: it }));
const leadExceptionReasonOptions = REGISTRATION_LEAD_EXCEPTION.map(it => ({ label: it, value: it }));
const lseOptions = LSE_VALUES.map(it => ({ label: it, value: it }));

interface IPjmRegistrationModalProps {
    value?: PjmRegistrationDto;
    seasonData?: PjmSeasonDto | null;
    onClose: (actionTaken: boolean) => void;
}

export const PjmRegistrationModal = ({ value, seasonData, onClose }: IPjmRegistrationModalProps) => {
    const { notification } = App.useApp();
    const { token: themeColors } = theme.useToken();
    const [form] = Form.useForm<PjmRegistrationDto>();

    const [_value, setValue] = useState(value);

    const registration = PjmRegistrationTransformer.toFormValues(_value!);
    const isNew = !registration?.uuid;

    const [formState, dispatch] = useReducer(reducer, defaultFormState);

    const formValues = Form.useWatch([], { form, preserve: true });
    const leadTime = Form.useWatch(['capacityInfo', 'leadTime'], form);

    const pjmRegistrationSubmitMutation = usePjmRegistrationSubmitMutation();
    const pjmRegistrationWithdrawMutation = usePjmRegistrationWithdrawMutation();
    const pjmRegistrationTerminateMutation = usePjmRegistrationTerminateMutation();
    const pjmRegistrationCalculateWplMutation = usePjmRegistrationCalculateWplMutation();
    const schedulePjmMeterDataUploadFileGenerationMutation = useSchedulePjmMeterDataUploadFileGenerationMutation();
    const pjmRegistrationCreateMutation = usePjmRegistrationCreateMutation();
    const pjmRegistrationUpdateMutation = usePjmRegistrationUpdateMutation();
    const isLoading = pjmRegistrationCreateMutation.isLoading || pjmRegistrationUpdateMutation.isLoading;

    const {
        data: latestPjmMeterDataUpload,
        isError: isFetchErrorLatestPjmMeterDataUpload,
        error: errorLatestPjmMeterDataUpload,
    } = useLatestPjmMeterDataUploadByRegistration(registration.uuid, { enabled: !!registration.id });

    if (isFetchErrorLatestPjmMeterDataUpload) {
        notification.error({
            key: makeKey('latest-meter-data-upload-fetch-error'),
            message: errorLatestPjmMeterDataUpload?.message || 'Cannot fetch latest PJM Meter Data Upload!',
        });
    }

    function onCommentsChange(data: PjmRegistrationDto['comments']) {
        form.setFieldValue('comments', data);
    }

    function onProgramChange(program: PjmRegistrationProgram) {
        console.info(`onProgramChange program=`, program);
        form.setFieldValue(['summaryInfo', 'startDate'], undefined);
        form.setFieldValue(['summaryInfo', 'endDate'], undefined);
    }

    async function onRegistrationSubmit() {
        try {
            await pjmRegistrationSubmitMutation.mutateAsync({ uuid: registration.uuid });
            notification.success({ key: makeKey('submit-notification'), message: 'PJM Registration submitted successfully!' });
            onClose(true);
        } catch (err: unknown) {
            notification.error({ key: makeKey('submit-notification'), message: (err as Error)?.message || 'Cannot submit PJM Registration!' });
        }
    }

    async function onRegistrationWithdraw() {
        try {
            await pjmRegistrationWithdrawMutation.mutateAsync({ uuid: registration.uuid });
            notification.success({ key: makeKey('withdraw-notification'), message: 'PJM Registration withdrawn successfully!' });
            onClose(true);
        } catch (err: unknown) {
            notification.error({ key: makeKey('withdraw-notification'), message: (err as Error)?.message || 'Cannot withdraw PJM Registration!' });
        }
    }

    async function onRegistrationTerminate() {
        try {
            await pjmRegistrationTerminateMutation.mutateAsync({ uuid: registration.uuid });
            notification.success({ key: makeKey('terminate-notification'), message: 'PJM Registration terminated successfully!' });
            onClose(true);
        } catch (err: unknown) {
            notification.error({ key: makeKey('terminate-notification'), message: (err as Error)?.message || 'Cannot terminate PJM Registration!' });
        }
    }

    async function onRegistrationCalculateWpl() {
        try {
            await pjmRegistrationCalculateWplMutation.mutateAsync({ uuid: registration.uuid });
            notification.success({ key: makeKey('calculate-wpl-notification'), message: 'WPL calculated successfully for PJM Registration!' });
            onClose(true);
        } catch (err: unknown) {
            notification.error({
                key: makeKey('calculate-wpl-notification'),
                message: (err as Error)?.message || 'Cannot calculate WPL for PJM Registration!',
            });
        }
    }

    async function onSchedulePjmMeterDataUploadFileGeneration() {
        try {
            await schedulePjmMeterDataUploadFileGenerationMutation.mutateAsync({
                registrationUuid: registration.uuid,
                isCalculateWinterPeakLoad: true,
                seasonId: seasonData!.id,
            });
            notification.success({
                key: makeKey('schedule-meter-data-upload-notification'),
                message: 'Meter Data file generation scheduled successfully!',
            });
            onClose(true);
        } catch (err: unknown) {
            notification.error({
                key: makeKey('schedule-meter-data-upload-notification-error'),
                message: (err as Error)?.message || 'Cannot schedule Meter Data file generation for PJM Registration!',
            });
        }
    }

    const disabledEffectiveDate: RangePickerProps['disabledDate'] = current => {
        // Can not select days before today and today
        return current && current < dayjs().endOf('day');
    };

    const disabledTerminationDate: RangePickerProps['disabledDate'] = current => {
        const effectiveDate = form.getFieldValue(['summaryInfo', 'startDate']);

        if (effectiveDate && current <= effectiveDate) {
            return true;
        }
        // Can not select days before today and today
        return current && current < dayjs().endOf('day');
    };

    // we should have season and latest meter data upload to be finished
    const disabledGenerateMeterDataFile = () => {
        return (
            !seasonData?.id ||
            isFetchErrorLatestPjmMeterDataUpload ||
            (!!latestPjmMeterDataUpload &&
                [MeterDataUploadStatus.READY_TO_GENERATE, MeterDataUploadStatus.PENDING].includes(latestPjmMeterDataUpload.status))
        );
    };

    const customPjmDateFormat: DatePickerProps['format'] = value => {
        return `${value.format('DD/MMM/YYYY')}`;
    };

    useEffect(() => {
        // init
        dispatch({ type: 'CHANGE', payload: form });
    }, []);

    useEffect(() => {
        dispatch({ type: 'CHANGE', payload: form });
    }, [formValues]);

    useEffect(() => {
        form.validateFields([['energyInfo', 'strikePrice']]);
    }, [leadTime]);

    async function onFormFinish(data: PjmRegistrationForm) {
        console.info(`PJM Registration onFormFinish`, data);

        const dto = data;

        try {
            if (isNew) {
                const createDto = PjmRegistrationTransformer.toCreateDto(dto);
                console.info('createDto: ', createDto);
                await pjmRegistrationCreateMutation.mutateAsync(createDto);
            } else {
                const updateDto = PjmRegistrationTransformer.toUpdateDto(dto);
                console.info('updateDto: ', updateDto);
                await pjmRegistrationUpdateMutation.mutateAsync({ uuid: dto.uuid, dto: updateDto });
            }

            notification.success({ key: makeKey('save-notification'), message: 'PJM Registration saved successfully!' });
            onClose(true);
        } catch (err: any) {
            notification.error({ key: makeKey('save-notification'), message: err?.message || 'Cannot save PJM Registration!' });
        }
    }

    async function onLocationChange(isUpdated: boolean = false) {
        if (!_value) return;

        const registrationUuid = _value.uuid;
        const updatedRegistration = await fetchPjmRegistration(registrationUuid);
        if (!updatedRegistration) return;

        setValue(updatedRegistration);

        form.setFieldValue(['locationSummaryInfo', 'capacityLossFactor'], updatedRegistration.locationSummaryInfo?.capacityLossFactor);
        form.setFieldValue(['locationInfo', 'capacityLossFactor'], updatedRegistration.locationInfo?.capacityLossFactor);

        form.setFieldValue(['locationSummaryInfo', 'energyLossFactor'], updatedRegistration.locationSummaryInfo?.energyLossFactor);
        form.setFieldValue(['locationInfo', 'energyLossFactor'], updatedRegistration.locationInfo?.energyLossFactor);

        form.setFieldValue(['locationSummaryInfo', 'energyLossFactor'], updatedRegistration.locationSummaryInfo?.energyLossFactor);
        form.setFieldValue(['locationInfo', 'energyLossFactor'], updatedRegistration.locationInfo?.energyLossFactor);

        form.setFieldValue(['summaryInfo', 'pricingPoint'], updatedRegistration.summaryInfo?.pricingPoint);

        form.setFieldValue(['locationSummaryInfo', 'peakLoadContribution'], updatedRegistration.locationSummaryInfo?.peakLoadContribution);
    }

    return (
        <Modal
            title={isNew ? 'Create PJM Registration' : 'Edit PJM Registration'}
            className="pjm-registration-modal"
            open
            width="80%"
            destroyOnHidden
            maskClosable={false}
            keyboard={false}
            onCancel={() => onClose(false)}
            footer={[
                <Button key={makeKey('cancel')} onClick={() => onClose(false)}>
                    Cancel
                </Button>,
                ...(isRegistrationActionAllowed(registration, 'generate_meter_data_file')
                    ? [
                          <Tooltip
                              title={
                                  disabledGenerateMeterDataFile()
                                      ? 'To generate Meter Data file, Peak Load Days must be defined and previous file generation must be completed.'
                                      : ''
                              }
                              key="generate-meter-data-tooltip"
                          >
                              <Button
                                  disabled={disabledGenerateMeterDataFile()}
                                  key={makeKey('generate-meter-data-file')}
                                  loading={schedulePjmMeterDataUploadFileGenerationMutation.isLoading}
                                  onClick={onSchedulePjmMeterDataUploadFileGeneration}
                              >
                                  Generate Meter Data file
                              </Button>
                          </Tooltip>,
                      ]
                    : []),
                ...(isRegistrationActionAllowed(registration, 'calculate_wpl')
                    ? [
                          <Button
                              key={makeKey('calculate-wpl')}
                              loading={pjmRegistrationCalculateWplMutation.isLoading}
                              onClick={onRegistrationCalculateWpl}
                          >
                              Calculate WPL
                          </Button>,
                      ]
                    : []),
                ...(isRegistrationActionAllowed(registration, 'submit')
                    ? [
                          <Button
                              key={makeKey('submit')}
                              type="primary"
                              loading={pjmRegistrationSubmitMutation.isLoading}
                              onClick={onRegistrationSubmit}
                          >
                              {registration.summaryInfo?.status === PjmRegistrationStatus.WITHDRAWN ? 'Resubmit' : 'Submit'}
                          </Button>,
                      ]
                    : []),
                ...(isRegistrationActionAllowed(registration, 'withdraw')
                    ? [
                          <Button
                              key={makeKey('withdraw')}
                              type="primary"
                              loading={pjmRegistrationWithdrawMutation.isLoading}
                              onClick={onRegistrationWithdraw}
                          >
                              Withdraw
                          </Button>,
                      ]
                    : []),
                ...(isRegistrationActionAllowed(registration, 'terminate')
                    ? [
                          <Button
                              key={makeKey('terminate')}
                              type="primary"
                              loading={pjmRegistrationTerminateMutation.isLoading}
                              onClick={onRegistrationTerminate}
                          >
                              Terminate
                          </Button>,
                      ]
                    : []),
                ...(isRegistrationActionAllowed(registration, 'save')
                    ? [
                          <Button key={makeKey('save')} type="primary" loading={isLoading} onClick={form.submit}>
                              Save
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <Form form={form} name="pjm-registration-form" preserve={false} layout="vertical" initialValues={registration} onFinish={onFormFinish}>
                {!isNew && <PjmSyncStatusDetailed value={registration!} />}
                {!isNew && !!latestPjmMeterDataUpload && <LatestPjmRegistrationMeterDataUploadStatus value={latestPjmMeterDataUpload} />}

                <div className="form-section">
                    <Form.Item name="uuid" hidden>
                        <Input />
                    </Form.Item>
                    <Row gutter={16} align="top">
                        <Col lg={12} xl={8}>
                            <Row gutter={8} align="top">
                                <Col span={12}>
                                    <Form.Item hidden name="uuid">
                                        <Input disabled />
                                    </Form.Item>
                                    <Form.Item hidden name="locationUuid">
                                        <Input disabled />
                                    </Form.Item>
                                    <Form.Item name="id" label={<Typography.Text strong>ID</Typography.Text>}>
                                        <Input disabled />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['summaryInfo', 'status']} label={<Typography.Text strong>Status</Typography.Text>}>
                                        <StatusTag status={registration.summaryInfo.status!} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={24}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'name']}
                                        label={<Typography.Text strong>Name</Typography.Text>}
                                        hasFeedback
                                        rules={[
                                            { required: true, message: 'Please enter registration name!' },
                                            { max: 40, message: 'Number of characters should be less than 40' },
                                        ]}
                                    >
                                        <Input placeholder="Registration" />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={8}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'CSP', 'name']}
                                        label={<Typography.Text strong>CSP</Typography.Text>}
                                    >
                                        <Input />
                                    </FormItemControl>
                                </Col>
                                <Col span={8}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'EDC', 'name']}
                                        label={<Typography.Text strong>EDC</Typography.Text>}
                                    >
                                        <Input />
                                    </FormItemControl>
                                </Col>
                                <Col span={8}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'LSE', 'name']}
                                        label={<Typography.Text strong>LSE</Typography.Text>}
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            autoClearSearchValue
                                            optionFilterProp="label"
                                            placeholder="Select LSE"
                                            options={lseOptions}
                                        />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'zone']}
                                        label={<Typography.Text strong>Zone</Typography.Text>}
                                    >
                                        <Input />
                                    </FormItemControl>
                                </Col>
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'pricingPoint']}
                                        label={<Typography.Text strong>Pricing Point</Typography.Text>}
                                        hasFeedback
                                        // rules={[{ required: true, message: 'Please select Pricing Point!' }]}
                                    >
                                        <Input />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['otherInfo', 'allowEDCtoModify']}
                                        label={<Typography.Text strong>Allow EDC to modify</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox></Checkbox>
                                    </FormItemControl>
                                </Col>
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['otherInfo', 'allowLSEtoModify']}
                                        label={<Typography.Text strong>Allow LSE to modify</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox></Checkbox>
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['otherInfo', 'holdScheduling']}
                                        label={<Typography.Text strong>Hold Scheduling</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox></Checkbox>
                                    </FormItemControl>
                                </Col>
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['otherInfo', 'holdSettlements']}
                                        label={<Typography.Text strong>Hold Settlements</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox></Checkbox>
                                    </FormItemControl>
                                </Col>
                            </Row>
                        </Col>
                        <Col lg={12} xl={8}>
                            <Row gutter={8} align="top">
                                <Col span={18}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'program']}
                                        label={<Typography.Text strong>Program</Typography.Text>}
                                        hasFeedback
                                        rules={[{ required: true, message: 'Please select program!' }]}
                                    >
                                        <Select
                                            style={{ width: '100%' }}
                                            showSearch
                                            autoClearSearchValue
                                            optionFilterProp="label"
                                            placeholder="Select Program"
                                            options={programOptions}
                                            onChange={onProgramChange}
                                        />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={18}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'startDate']}
                                        label={<Typography.Text strong>Effective Date</Typography.Text>}
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format={customPjmDateFormat}
                                            disabledDate={disabledEffectiveDate}
                                            allowClear={false}
                                        />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={18}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'endDate']}
                                        label={<Typography.Text strong>Termination Date</Typography.Text>}
                                    >
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format={customPjmDateFormat}
                                            disabledDate={disabledTerminationDate}
                                            allowClear={false}
                                            onChange={value => {
                                                // set endDate to the end of the day
                                                form.setFieldValue(
                                                    ['summaryInfo', 'endDate'],
                                                    value ? value.tz(PJM_TZ).endOf('day').subtract(1, 'second') : null
                                                );
                                            }}
                                        />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'residential']}
                                        label={<Typography.Text strong>Residential Registration?</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox />
                                    </FormItemControl>
                                </Col>
                            </Row>

                            <Row gutter={8} align="top">
                                <Col span={18}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'residentialProgramType']}
                                        label={<Typography.Text strong>Type</Typography.Text>}
                                        rules={[{ required: true }, createResidentialProgramTypeValidator]}
                                    >
                                        <ResidentialProgramTypeSelector />
                                    </FormItemControl>
                                </Col>
                            </Row>
                            <Row gutter={8} align="top">
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'loadDataSample']}
                                        label={<Typography.Text strong>Load Data Sample?</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox />
                                    </FormItemControl>
                                </Col>
                                <Col span={12}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['summaryInfo', 'sampleNumberParticipants']}
                                        label={<Typography.Text strong># Sample Participants</Typography.Text>}
                                    >
                                        <InputNumber />
                                    </FormItemControl>
                                </Col>
                            </Row>

                            {formState['summaryInfo.testRequired'].state !== 'hidden' && (
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <FormItemControl
                                            formState={formState}
                                            name={['summaryInfo', 'testRequired']}
                                            label={<Typography.Text strong>Test Required</Typography.Text>}
                                            valuePropName="checked"
                                        >
                                            <Checkbox />
                                        </FormItemControl>
                                    </Col>
                                </Row>
                            )}

                            {formState['summaryInfo.completed'].state !== 'hidden' && (
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <FormItemControl
                                            formState={formState}
                                            name={['summaryInfo', 'completed']}
                                            label={<Typography.Text strong>Completed</Typography.Text>}
                                            valuePropName="checked"
                                        >
                                            <Checkbox />
                                        </FormItemControl>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                        <Col lg={0} xl={8}></Col>
                    </Row>
                </div>

                {formState['priceResponsiveDemandSection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Price Responsive Demand
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={8}>
                                <FormItemControl
                                    formState={formState}
                                    name={['priceResponsiveDemandInfo', 'dynamicRetailRate']}
                                    label={<Typography.Text strong>CSP certifies this registration is on a dynamic retail rate</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                                <FormItemControl
                                    formState={formState}
                                    name={['priceResponsiveDemandInfo', 'scadaAvailable']}
                                    label={<Typography.Text strong>CSP certifies SCADA is available for PRD</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}

                {formState['capacitySection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Capacity
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'peakLoadContribution']}
                                    label={<Typography.Text strong>Peak Load Contribution (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'winterPeakLoad']}
                                    label={<Typography.Text strong>Winter Peak Load (kW)</Typography.Text>}
                                >
                                    <InputNumber disabled min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'resourceType']}
                                    label={<Typography.Text strong>Resource Type</Typography.Text>}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        autoClearSearchValue
                                        optionFilterProp="label"
                                        placeholder="Select Resource Type"
                                        options={resourceTypeOptions}
                                    />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'measurementMethod']}
                                    label={<Typography.Text strong>Measurement Method</Typography.Text>}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        autoClearSearchValue
                                        optionFilterProp="label"
                                        placeholder="Select Measurement Method"
                                        options={measurementMethodOptions}
                                    />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'winterManagedLoad']}
                                    label={<Typography.Text strong>Winter Managed Load (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'product']}
                                    label={<Typography.Text strong>Product</Typography.Text>}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        autoClearSearchValue
                                        optionFilterProp="label"
                                        placeholder="Select Product"
                                        options={productOptions}
                                    />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'summerManagedLoad']}
                                    label={<Typography.Text strong>Summer Managed Load (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'winterWeatherAdjustmentFactor']}
                                    label={<Typography.Text strong>Winter Weather Adj. Factor</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'RPMResourceID']}
                                    label={
                                        <Tooltip title="Please find the correct ID on the DRHUB Reports page.">
                                            <Typography.Text strong>RPM DR Resource ID</Typography.Text>
                                        </Tooltip>
                                    }
                                >
                                    <Input style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'capacityLossFactor']}
                                    label={<Typography.Text strong>Capacity Loss Factor</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'winterNominatedICAP']}
                                    label={<Typography.Text strong>Winter Nominated ICAP (kW)</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'leadTime']}
                                    label={<Typography.Text strong>Lead Time</Typography.Text>}
                                >
                                    <Select autoClearSearchValue optionFilterProp="label" placeholder="Lead Time" options={leadTimeOptions} />
                                </FormItemControl>
                                {['Short_60', 'Long_120'].includes(leadTime) && (
                                    <>
                                        <Row gutter={8} align="bottom">
                                            <Col span={24}>
                                                <FormItemControl
                                                    formState={formState}
                                                    name={['capacityInfo', 'leadExceptionCertify']}
                                                    label={
                                                        <Typography.Text strong>
                                                            My company has necessary documentation to support the 30 minute notification exception in
                                                            accordance with PJM tariff and rules
                                                        </Typography.Text>
                                                    }
                                                    valuePropName="checked"
                                                >
                                                    <Checkbox></Checkbox>
                                                </FormItemControl>
                                            </Col>
                                        </Row>
                                        <Row gutter={8} align="bottom">
                                            <Col span={24}>
                                                <FormItemControl
                                                    formState={formState}
                                                    name={['capacityInfo', 'leadExceptionReason']}
                                                    label={<Typography.Text strong>30 Minute Lead Exception</Typography.Text>}
                                                >
                                                    <Select
                                                        allowClear
                                                        autoClearSearchValue
                                                        optionFilterProp="label"
                                                        placeholder="Select Lead Exception"
                                                        options={leadExceptionReasonOptions}
                                                    />
                                                </FormItemControl>
                                            </Col>
                                        </Row>
                                        <Row gutter={8} align="bottom">
                                            <Col span={24}>
                                                <FormItemControl
                                                    formState={formState}
                                                    name={['capacityInfo', 'leadExceptionDesc']}
                                                    label={<Typography.Text strong>30 Minute Lead Exception Description </Typography.Text>}
                                                >
                                                    <TextArea />
                                                </FormItemControl>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Col>
                        </Row>

                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'summerNominatedICAP']}
                                    label={<Typography.Text strong>Summer Nominated ICAP (kW)</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        </Row>

                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'summerOnly']}
                                    label={
                                        <>
                                            <Typography.Text strong>Summer Only&nbsp;</Typography.Text>
                                            <Tooltip title="By checking this box, the user confirms this registration is being used to fulfill a Summer Only RPM pr FRR Commitment and is required to test if not required to respond to dispatch in the summer">
                                                <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                            </Tooltip>
                                        </>
                                    }
                                    valuePropName="checked"
                                >
                                    <Checkbox></Checkbox>
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'participantCount']}
                                    label={<Typography.Text strong>No. of Participants</Typography.Text>}
                                >
                                    <InputNumber />
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}
                {formState['capacityPrdSection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Capacity
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'peakLoadContribution']}
                                    label={<Typography.Text strong>Peak Load Contribution (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'measurementMethod']}
                                    label={<Typography.Text strong>Measurement Method</Typography.Text>}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        autoClearSearchValue
                                        optionFilterProp="label"
                                        placeholder="Select Measurement Method"
                                        options={measurementMethodOptions}
                                    />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'summerManagedLoad']}
                                    label={<Typography.Text strong>Summer Managed Load (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'capacityLossFactor']}
                                    label={<Typography.Text strong>Capacity Loss Factor</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'summerNominatedICAP']}
                                    label={<Typography.Text strong>Summer Nominated ICAP (kW)</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['capacityInfo', 'participantCount']}
                                    label={<Typography.Text strong>No. of Participants</Typography.Text>}
                                >
                                    <InputNumber />
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}
                {formState['energySection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Energy
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'strikePrice']}
                                    label={<Typography.Text strong>Strike Price ($/Mwh)</Typography.Text>}
                                    rules={[createStrikePriceValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'shutdownCosts']}
                                    label={<Typography.Text strong>Shut Down Costs ($)</Typography.Text>}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={99999} />
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}
                {formState['marketsSection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Markets
                        </Divider>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'realTime']}
                                    label={<Typography.Text strong>RT Energy</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'regulation']}
                                    label={<Typography.Text strong>Regulation</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'synchronizedReserve']}
                                    label={<Typography.Text strong>Synchronized Reserve</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'secondaryReserve']}
                                    label={<Typography.Text strong>Secondary Reserve</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'dayAhead']}
                                    label={<Typography.Text strong>DA Energy</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}></Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'inflexible']}
                                    label={<Typography.Text strong>Inflexible</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'flexible']}
                                    label={<Typography.Text strong>Flexible</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'batchLoad']}
                                    label={<Typography.Text strong>Batch Load</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}
                {
                    // !isNew && (
                    //     <div className="form-section">
                    //         <Divider orientation="left" orientationMargin="0">
                    //             CBL Test
                    //         </Divider>
                    //         <Row gutter={8} align="bottom" justify="space-between" className="toolbar">
                    //             <Col span={8}>
                    //                 <Form.Item name={['cblMethodInfo', 'methodName']} label={<Typography.Text strong>CBL Method</Typography.Text>}>
                    //                     <Input disabled />
                    //                 </Form.Item>
                    //             </Col>
                    //             {/* <Col span={16} >
                    //             <Button disabled>Upload Meter Data</Button>
                    //         </Col> */}
                    //         </Row>
                    //     </div>
                    // )
                }
                {formState['synchronizedReserveSection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Synchronized Reserve
                        </Divider>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'srArea']}
                                    label={<Typography.Text strong>SR Area</Typography.Text>}
                                >
                                    <Input />
                                </FormItemControl>
                            </Col>
                            {formState['energyInfo.srSCADATestedAndVerified'].state !== 'hidden' && (
                                <Col span={4}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['energyInfo', 'srSCADATestedAndVerified']}
                                        label={<Typography.Text strong>SCADA Tested and Verified</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox />
                                    </FormItemControl>
                                </Col>
                            )}
                            <Col span={4}>
                                <FormItemControl formState={formState} label={' '}>
                                    <Button title="Sorry, uploading documents is not supported" icon={<PlusOutlined />} disabled>
                                        SR Information
                                    </Button>
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}
                {formState['secondaryReserveSection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Secondary Reserve
                        </Divider>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'secrArea']}
                                    label={<Typography.Text strong>SECR Area</Typography.Text>}
                                >
                                    <Input />
                                </FormItemControl>
                            </Col>
                            {formState['energyInfo.secrSCADATestedAndVerified'].state !== 'hidden' && (
                                <Col span={4}>
                                    <FormItemControl
                                        formState={formState}
                                        name={['energyInfo', 'secrSCADATestedAndVerified']}
                                        label={<Typography.Text strong>SCADA Tested and Verified</Typography.Text>}
                                        valuePropName="checked"
                                    >
                                        <Checkbox />
                                    </FormItemControl>
                                </Col>
                            )}
                            <Col span={4}>
                                <FormItemControl formState={formState} label={' '}>
                                    <Button title="Sorry, uploading documents is not supported" icon={<PlusOutlined />} disabled>
                                        SECR Information
                                    </Button>
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}
                {formState['regulationSection'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Regulation
                        </Divider>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'regulationSCADAName']}
                                    label={<Typography.Text strong>SCADA Name</Typography.Text>}
                                >
                                    <Input />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'regulationSCADATestedAndVerified']}
                                    label={<Typography.Text strong>SCADA Tested and Verified</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'regulationTestCompleted']}
                                    label={<Typography.Text strong>Regulation Test Completed</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl formState={formState} label={' '}>
                                    <Button title="Sorry, uploading documents is not supported" icon={<PlusOutlined />} disabled>
                                        Regulation Plan Documentation
                                    </Button>
                                </FormItemControl>
                            </Col>
                        </Row>
                        <Row gutter={8} align="top">
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'submeter']}
                                    label={<Typography.Text strong>Submeter</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={8}>
                                <FormItemControl
                                    formState={formState}
                                    name={['energyInfo', 'regulationCertify']}
                                    label={
                                        <Typography.Text strong>I certify that submeter data includes all similar end-use devices</Typography.Text>
                                    }
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </FormItemControl>
                            </Col>
                            <Col span={4}>
                                <FormItemControl formState={formState} label={' '}>
                                    <Button title="Sorry, uploading documents is not supported" icon={<PlusOutlined />} disabled>
                                        One-Line Diagram Documentation
                                    </Button>
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}

                {formState['peakShavingProgram'].state !== 'hidden' && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Peak Shaving Program
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={6}>
                                <FormItemControl
                                    formState={formState}
                                    name={['peakShavingProgram']}
                                    label={<Typography.Text strong>Peak Shaving Program</Typography.Text>}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        autoClearSearchValue
                                        optionFilterProp="label"
                                        placeholder="Select Peak Shaving Program"
                                        options={[]}
                                    />
                                </FormItemControl>
                            </Col>
                        </Row>
                    </div>
                )}

                <div className="form-section">
                    <Divider orientation="left" orientationMargin="0">
                        Locations
                    </Divider>
                    <Row gutter={8} align="bottom">
                        {formState['locationSummaryInfo.capacityLossFactor'].state !== 'hidden' && (
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'capacityLossFactor']}
                                    label={<Typography.Text strong>Capacity Loss Factor</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        )}
                        {formState['locationSummaryInfo.energyLossFactor'].state !== 'hidden' && (
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'energyLossFactor']}
                                    label={<Typography.Text strong>Energy Loss Factor</Typography.Text>}
                                >
                                    <InputNumber step={0.1} min={0} max={1.99999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        )}
                        {formState['locationSummaryInfo.peakLoadContribution'].state !== 'hidden' && (
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'peakLoadContribution']}
                                    label={<Typography.Text strong>Peak Load Contribution (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        )}
                        {formState['locationSummaryInfo.winterPeakLoad'].state !== 'hidden' && (
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'winterPeakLoad']}
                                    label={<Typography.Text strong>Winter Peak Load (kW)</Typography.Text>}
                                >
                                    <InputNumber min={0} max={9999999.999} style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        )}
                        {formState['locationSummaryInfo.maximumLoadReductionCapability'].state !== 'hidden' && (
                            <Col span={4}>
                                <FormItemControl
                                    formState={formState}
                                    name={['locationSummaryInfo', 'maximumLoadReductionCapability']}
                                    label={<Typography.Text strong>Maximum Load Reduction Capability (kW)</Typography.Text>}
                                >
                                    <InputNumber style={{ width: '100%' }} />
                                </FormItemControl>
                            </Col>
                        )}
                    </Row>
                    <RegistrationLocationTable value={registration} key={makeKey('location-table')} onLocationChange={onLocationChange} />
                </div>
                {!isNew && (
                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Comments
                        </Divider>
                        <FormItemControl formState={formState} name={['comments']} noStyle>
                            <RegistrationCommentTable
                                key={makeKey('comment-table')}
                                value={form.getFieldValue('comments') || { comment: [] }}
                                onChange={onCommentsChange}
                            />
                        </FormItemControl>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

function makeKey(label: string) {
    return `pjm-registration-modal-${label}`;
}

function createStrikePriceValidator({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) {
    return {
        validator(_rule: RuleObject, value: number) {
            const leadTime = getFieldValue(['capacityInfo', 'leadTime']) as PjmRegistrationLeadTime | undefined;

            const result = strikePrice.isValid(value, leadTime);
            if (result.valid) return Promise.resolve();

            return Promise.reject(result.error);
        },
    };
}

function createResidentialProgramTypeValidator({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) {
    return {
        validator(_rule: RuleObject, value: string) {
            if (value == null && getFieldValue(['summaryInfo', 'residential']) === true) {
                return Promise.reject(new Error('Please select type!'));
            }
            return Promise.resolve();
        },
    };
}
